import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Bell, CalendarClock, CheckCircle2, Copy, MessageCircle, RefreshCw, Search } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';

type ReminderType =
  | 'follow_up'
  | 'birthday'
  | 'anniversary'
  | 'passport_expiry'
  | 'visa_expiry'
  | 'document_expiry'
  | 'travel_departure';

type ReminderItem = {
  id: string;
  type: ReminderType;
  title: string;
  personName: string;
  phone: string;
  date: string;
  source: string;
  sourceId: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'done' | 'dismissed';
  message: string;
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isoDate(value: unknown) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (typeof value === 'object' && value && 'toDate' in value && typeof (value as any).toDate === 'function') {
    return (value as any).toDate().toISOString().slice(0, 10);
  }
  return '';
}

function daysBetween(dateString: string) {
  if (!dateString) return 999999;
  const today = new Date(todayIso() + 'T00:00:00');
  const target = new Date(dateString + 'T00:00:00');
  if (Number.isNaN(target.getTime())) return 999999;
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

function monthDayWithinNextDays(dateString: string, daysWindow: number) {
  if (!dateString) return false;

  const today = new Date(todayIso() + 'T00:00:00');
  const end = addDays(today, daysWindow);
  const raw = new Date(dateString + 'T00:00:00');

  if (Number.isNaN(raw.getTime())) return false;

  const thisYear = new Date(today.getFullYear(), raw.getMonth(), raw.getDate());
  const nextYear = new Date(today.getFullYear() + 1, raw.getMonth(), raw.getDate());

  return (thisYear >= today && thisYear <= end) || (nextYear >= today && nextYear <= end);
}

function monthDayDisplay(dateString: string) {
  if (!dateString) return '';
  const raw = new Date(dateString + 'T00:00:00');
  if (Number.isNaN(raw.getTime())) return dateString;
  const year = new Date().getFullYear();
  const d = new Date(year, raw.getMonth(), raw.getDate());
  if (d < new Date(todayIso() + 'T00:00:00')) d.setFullYear(year + 1);
  return d.toISOString().slice(0, 10);
}

function buildWhatsAppText(item: ReminderItem) {
  if (item.type === 'birthday') {
    return `Happy Birthday ${item.personName}! 🎉\nBest wishes from Sardar Patel Travel.`;
  }

  if (item.type === 'anniversary') {
    return `Happy Anniversary ${item.personName}! 🎉\nBest wishes from Sardar Patel Travel.`;
  }

  if (item.type === 'passport_expiry') {
    return `Hello ${item.personName}, reminder from Sardar Patel Travel: your passport expiry date is ${item.date}. Please renew it on time for future travel.`;
  }

  if (item.type === 'visa_expiry') {
    return `Hello ${item.personName}, reminder from Sardar Patel Travel: your visa expiry date is ${item.date}. Please check renewal requirements.`;
  }

  if (item.type === 'follow_up') {
    return `Hello ${item.personName}, this is a follow-up from Sardar Patel Travel regarding your travel inquiry. Please let us know how we can assist you further.`;
  }

  if (item.type === 'document_expiry') {
    return `Hello ${item.personName}, reminder from Sardar Patel Travel: your document "${item.title}" is expiring on ${item.date}.`;
  }

  return item.message || `Hello ${item.personName}, reminder from Sardar Patel Travel.`;
}

async function loadInquiries(): Promise<ReminderItem[]> {
  const rows: ReminderItem[] = [];
  const snap = await getDocs(collection(db, 'inquiries'));

  snap.docs.forEach((d) => {
    const x = d.data() as any;
    const follow = isoDate(x.nextFollowUpDate || x.followUp);
    const stage = String(x.stage || x.status || '').toLowerCase();
    const isActive = !['converted', 'not_converted', 'closed'].includes(stage);
    const due = daysBetween(follow);

    if (follow && due <= 7 && isActive) {
      const name = String(x.clientName || x.clientSnapshot?.name || x.fullName || 'Client');
      rows.push({
        id: `follow_${d.id}`,
        type: 'follow_up',
        title: `Follow-up due: ${name}`,
        personName: name,
        phone: String(x.phone || x.clientSnapshot?.phone || ''),
        date: follow,
        source: 'CRM Inquiry',
        sourceId: d.id,
        priority: due < 0 ? 'urgent' : due <= 1 ? 'high' : 'medium',
        status: 'pending',
        message: ''
      });
    }
  });

  return rows;
}

async function loadClients(): Promise<ReminderItem[]> {
  const rows: ReminderItem[] = [];
  const snap = await getDocs(collection(db, 'clients'));

  snap.docs.forEach((d) => {
    const x = d.data() as any;
    const first = String(x.firstName || '').trim();
    const last = String(x.lastName || '').trim();
    const name = String(x.fullName || x.name || `${first} ${last}`.trim() || 'Client');
    const phone = String(x.phone || x.phoneFull || x.phoneNumber || '');

    const birth = isoDate(x.birthDate || x.dob);
    if (monthDayWithinNextDays(birth, 30)) {
      rows.push({
        id: `birthday_${d.id}`,
        type: 'birthday',
        title: `Birthday: ${name}`,
        personName: name,
        phone,
        date: monthDayDisplay(birth),
        source: 'Client Master',
        sourceId: d.id,
        priority: 'medium',
        status: 'pending',
        message: ''
      });
    }

    const anniv = isoDate(x.anniversary || x.anniversaryDate);
    if (monthDayWithinNextDays(anniv, 30)) {
      rows.push({
        id: `anniv_${d.id}`,
        type: 'anniversary',
        title: `Anniversary: ${name}`,
        personName: name,
        phone,
        date: monthDayDisplay(anniv),
        source: 'Client Master',
        sourceId: d.id,
        priority: 'medium',
        status: 'pending',
        message: ''
      });
    }

    const passport = isoDate(x.passportExpiry || x.passportExpiryDate || x.passport?.expiryDate);
    const passportDays = daysBetween(passport);
    if (passport && passportDays >= 0 && passportDays <= 240) {
      rows.push({
        id: `passport_${d.id}`,
        type: 'passport_expiry',
        title: `Passport expiring: ${name}`,
        personName: name,
        phone,
        date: passport,
        source: 'Client Master',
        sourceId: d.id,
        priority: passportDays <= 30 ? 'urgent' : passportDays <= 90 ? 'high' : 'medium',
        status: 'pending',
        message: ''
      });
    }

    const visa = isoDate(x.visaExpiry || x.visaExpiryDate);
    const visaDays = daysBetween(visa);
    if (visa && visaDays >= 0 && visaDays <= 180) {
      rows.push({
        id: `visa_${d.id}`,
        type: 'visa_expiry',
        title: `Visa expiring: ${name}`,
        personName: name,
        phone,
        date: visa,
        source: 'Client Master',
        sourceId: d.id,
        priority: visaDays <= 30 ? 'urgent' : visaDays <= 90 ? 'high' : 'medium',
        status: 'pending',
        message: ''
      });
    }
  });

  return rows;
}

async function loadDocuments(): Promise<ReminderItem[]> {
  const rows: ReminderItem[] = [];
  const snap = await getDocs(collection(db, 'documents'));

  snap.docs.forEach((d) => {
    const x = d.data() as any;
    const expiry = isoDate(x.expiryDate);
    const expDays = daysBetween(expiry);

    if (expiry && expDays >= 0 && expDays <= 240) {
      const name = String(x.ownerName || 'Document Owner');
      rows.push({
        id: `doc_${d.id}`,
        type: 'document_expiry',
        title: `Document expiring: ${x.documentFor || x.title || x.fileName || 'Document'}`,
        personName: name,
        phone: '',
        date: expiry,
        source: 'Documents',
        sourceId: d.id,
        priority: expDays <= 30 ? 'urgent' : expDays <= 90 ? 'high' : 'medium',
        status: 'pending',
        message: ''
      });
    }
  });

  return rows;
}

function sortReminders(a: ReminderItem, b: ReminderItem) {
  const priorityRank = { urgent: 0, high: 1, medium: 2, low: 3 };
  const p = priorityRank[a.priority] - priorityRank[b.priority];
  if (p !== 0) return p;
  return daysBetween(a.date) - daysBetween(b.date);
}

export default function NotificationsPage({ user }: { user: ERPUser }) {
  const [items, setItems] = useState<ReminderItem[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | ReminderType>('all');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  async function loadAll() {
    setLoading(true);
    setMessage('');

    try {
      const [inq, clients, docs] = await Promise.all([
        loadInquiries().catch(() => []),
        loadClients().catch(() => []),
        loadDocuments().catch(() => [])
      ]);

      setItems([...inq, ...clients, ...docs].sort(sortReminders));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to load reminders.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return items.filter((item) => {
      const typeOk = typeFilter === 'all' || item.type === typeFilter;
      const textOk = !q || [
        item.title,
        item.personName,
        item.phone,
        item.type,
        item.source,
        item.date
      ].join(' ').toLowerCase().includes(q);

      return typeOk && textOk;
    });
  }, [items, search, typeFilter]);

  const counts = useMemo(() => {
    return {
      total: items.length,
      urgent: items.filter((x) => x.priority === 'urgent').length,
      follow: items.filter((x) => x.type === 'follow_up').length,
      birthday: items.filter((x) => x.type === 'birthday' || x.type === 'anniversary').length,
      document: items.filter((x) => x.type === 'document_expiry' || x.type === 'passport_expiry' || x.type === 'visa_expiry').length
    };
  }, [items]);

  async function copyWhatsApp(item: ReminderItem) {
    const text = buildWhatsAppText(item);

    try {
      await navigator.clipboard.writeText(text);
      setMessage(`WhatsApp message copied for ${item.personName}.`);
    } catch {
      setMessage(text);
    }
  }

  function openWhatsApp(item: ReminderItem) {
    const text = encodeURIComponent(buildWhatsAppText(item));
    const rawPhone = item.phone.replace(/[^0-9]/g, '');

    if (!rawPhone) {
      setMessage('Phone number not available. Message copied instead.');
      copyWhatsApp(item);
      return;
    }

    window.open(`https://wa.me/${rawPhone}?text=${text}`, '_blank');
  }

  const reminderTypes: ReminderType[] = [
    'follow_up',
    'birthday',
    'anniversary',
    'passport_expiry',
    'visa_expiry',
    'document_expiry',
    'travel_departure'
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Phase 6.1 Real Release</p>
          <h1>Notifications + Reminders + WhatsApp</h1>
          <p>Daily action center for CRM follow-ups, client reminders, expiring documents, and WhatsApp-ready actions.</p>
        </div>

        <button className="btn" title="Refresh reminders" onClick={loadAll}>
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      {message && (
        <div className="error-box" style={{ marginBottom: 20 }}>
          {message}
        </div>
      )}

      <div className="reminder-top-grid">
        <section className="card reminder-card">
          <h3>Total Reminders</h3>
          <p className="muted"><b>{counts.total}</b> active reminder(s)</p>
        </section>
        <section className="card reminder-card">
          <h3>Urgent</h3>
          <p className="muted"><b>{counts.urgent}</b> needs quick action</p>
        </section>
        <section className="card reminder-card">
          <h3>CRM Follow-ups</h3>
          <p className="muted"><b>{counts.follow}</b> due / upcoming</p>
        </section>
        <section className="card reminder-card">
          <h3>Client Dates</h3>
          <p className="muted"><b>{counts.birthday}</b> birthday / anniversary</p>
        </section>
        <section className="card reminder-card">
          <h3>Documents</h3>
          <p className="muted"><b>{counts.document}</b> passport / visa / document expiry</p>
        </section>
      </div>

      <div className="toolbar" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 16, top: 18, color: '#66758a' }} />
          <input
            style={{ paddingLeft: 44 }}
            placeholder="Search reminder, client, phone, type, source"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as 'all' | ReminderType)}>
          <option value="all">All Reminder Types</option>
          {reminderTypes.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </div>

      <section className="card table-card" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Reminder</th>
              <th>Person</th>
              <th>Date</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Source</th>
              <th>WhatsApp</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="empty">Loading reminders...</td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">No reminders found.</td>
              </tr>
            )}

            {!loading && filtered.map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.title}</strong>
                  <br />
                  <small>{daysBetween(item.date) < 0 ? `${Math.abs(daysBetween(item.date))} day(s) overdue` : `${daysBetween(item.date)} day(s) left`}</small>
                </td>
                <td>
                  {item.personName || '-'}
                  <br />
                  <small>{item.phone || '-'}</small>
                </td>
                <td>{item.date || '-'}</td>
                <td><span className="pill">{item.type}</span></td>
                <td><span className="pill">{item.priority}</span></td>
                <td>{item.source}</td>
                <td className="actions">
                  <button title="Copy WhatsApp message" onClick={() => copyWhatsApp(item)}>
                    <Copy size={16} />
                  </button>
                  <button title="Open WhatsApp with message" onClick={() => openWhatsApp(item)}>
                    <MessageCircle size={16} />
                  </button>
                </td>
                <td>
                  <button className="btn" title="Mark done visual only for this release" onClick={() => setMessage('Done action will be saved to Firestore in Phase 6.2.')}>
                    <CheckCircle2 size={16} /> Done
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card" style={{ marginTop: 20 }}>
        <h3><Bell size={18} /> Phase 6.1 Notes</h3>
        <p className="muted">
          This release reads existing CRM, Client Master, and Documents data to create live reminders.
          Phase 6.2 will save done/dismissed statuses and add automated notification records.
        </p>
      </section>
    </div>
  );
}
